import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { RootState } from '../../store/store';
import {
  useLazyGetMeQuery,
  useRefreshTokenMutation,
} from '../../store/services/authService';
import { getRefreshToken } from '../../lib/auth';
import LoaderSpin from '../atoms/LoaderSpin';

export function AuthGaurd() {
  const user = useSelector((state: RootState) => state.auth.user);
  const token = useSelector((state: RootState) => state.auth.token);
  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated
  );
  const location = useLocation();
  const [getMe, { isLoading: isGetMeLoading }] = useLazyGetMeQuery();
  const [refreshToken, { isLoading: isRefreshLoading }] =
    useRefreshTokenMutation();
  const [isInitializing, setIsInitializing] = useState(true);
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const initializeAuth = async () => {
      try {
        if (token && user) {
          setIsInitializing(false);
          return;
        }

        if (token && !user) {
          const result = await getMe();
          if (result.error) {
            console.error('Failed to get user data:', result.error);
          }
          setIsInitializing(false);
          return;
        }

        if (!token) {
          const storedRefreshToken = getRefreshToken();
          if (storedRefreshToken) {
            try {
              await refreshToken({ refreshToken: storedRefreshToken }).unwrap();
              const result = await getMe();
              if (result.error) {
                console.error(
                  'Failed to get user data after refresh:',
                  result.error
                );
              }
            } catch (error) {
              console.error(
                'Failed to refresh token on initialization:',
                error
              );
            }
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        setIsInitializing(false);
      }
    };

    initializeAuth();
  }, [token, user, getMe, refreshToken]);

  if (isInitializing || isGetMeLoading || isRefreshLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoaderSpin size="large" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <Outlet />;
}
